import { SYNC_MODE_STALE_MS } from "components/FlowEditor/Toolbox/useSalesforceV2Access";
import { planIncludesSalesforce } from "components/FlowEditor/Toolbox/salesforceV2Tools";
import { useQuery } from "react-query";
import {
  getSalesforceSyncSettings,
  SalesforceSyncRequestError,
  SalesforceSyncSettings,
} from "store/persistence/persistSalesforceSync";
import { salesforceSyncQueryKeys } from "store/react-query/queryKeys";
import { useStores } from "store/useStore";

/**
 * Does the account's plan have company objects (the Companies feature, as it
 * always was)? The one place that reads the flag, for the pages and for the
 * parts of a company page that only the plan opens.
 */
export const hasCompanyPlan = (account?: {
  hasCompanyObjectsFeature?: boolean;
}): boolean => Boolean(account?.hasCompanyObjectsFeature);

/**
 * Whether the company pages (the Companies menu entry and /objects/company) are
 * open to the account.
 *
 * They are open to every account whose plan has company objects, as they always
 * were. And, now, to an account on Salesforce sync v2 that has Salesforce in its
 * plan: Salesforce is one package, and what the Salesforce sync makes (its
 * companies, its Opportunities) must be usable by whoever pays for it, whatever
 * else their plan leaves out. An AppSumo account with the Salesforce add-on has
 * Companies switched off by default, and without this its sync would fill a page
 * it cannot open.
 *
 * Open this way, the page is for what Salesforce made, not for the Companies
 * feature: it lists the companies, shows one, and has segments, folders and
 * associations, but there is no "Add Company" and no "Add Custom Field" on it
 * (AddObjectDropdown asks `hasCompanyPlan`). Making companies by hand and adding
 * fields to them stay with the plan. Nothing else about the gating of Companies
 * or of custom objects changes.
 *
 * `pending` is true while the sync mode is being read for an account that needs
 * it to decide, so a page does not send the account away before it knows.
 *
 * Call it from a component that renders again when the account store changes (an
 * observer), since it reads the account store.
 */
export const useCompanyPagesAllowed = (): {
  allowed: boolean;
  pending: boolean;
} => {
  const { accountStore } = useStores();
  const account = accountStore.account;
  const byPlan = hasCompanyPlan(account);
  const connected = Boolean(account?.services?.salesforce);
  const salesforceInPlan = planIncludesSalesforce({
    tier: accountStore.accountTier,
    canUseSalesforce: account?.payment?.appsumo?.canUseSalesforce,
  });
  const needsMode = !byPlan && connected && salesforceInPlan;
  // the same call and cache entry as the flow editor's toolbox and the Data
  // Sync page: the mode is read once and kept
  const settings = useQuery<SalesforceSyncSettings, SalesforceSyncRequestError>(
    salesforceSyncQueryKeys.settings(),
    getSalesforceSyncSettings,
    {
      enabled: needsMode,
      staleTime: SYNC_MODE_STALE_MS,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) =>
        (error as SalesforceSyncRequestError | null | undefined)?.status !==
          403 && failureCount < 2,
    }
  );
  if (byPlan) return { allowed: true, pending: false };
  if (!needsMode) return { allowed: false, pending: false };
  if (settings.isLoading) return { allowed: false, pending: true };
  return { allowed: settings.data?.mode === "v2", pending: false };
};
